import {
  defineToggle,
  featureToggleClient,
  type FeatureToggleUser,
} from './toggle-client'
import {
  type Observable,
  combineLatestWith,
  concatMap,
  distinctUntilChanged,
  map,
  switchMap,
  firstValueFrom,
} from 'rxjs'
import { type UserData } from '@/features/iam/users/UserData'
import { type User } from '@/features/iam/users/User'
import { Id } from '@/infra/id'
import { toStream } from '@/utils/mobx-util'
import { filterNonNil, shareLatest } from '@/utils/rx-util'

/**
 * This contains our list of active toggles. Can be imported directly
 * by consumers.
 */
export const FeatureToggles = {
  callCenter: defineToggle('Arphyx.CallCenterEnabled', false),
  paymentMethods: defineToggle('Portals.UseStripePaymentMethods', false),
}

export interface FeatureTogglesOptions {
  /**
   * Emits the current user when available.
   */
  currentUser$: Observable<User>

  /**
   * Emits the current user data when available.
   */
  currentUserData$: Observable<UserData>
}

/**
 * A feature for managing... features.
 *
 * So fucking meta.
 */
export function FeatureTogglesFeature(options: FeatureTogglesOptions) {
  // Get a stream for the current user, but only once an email address
  // is available.
  const currentUser$ = options.currentUser$.pipe(
    switchMap((user) =>
      toStream(() => user.email, true).pipe(
        filterNonNil(),
        map(() => user),
      ),
    ),
    distinctUntilChanged(),
  )

  // We've got a toggle user once the user data is available
  // and when the user's email is available.
  const featureToggleUsers$ = currentUser$.pipe(
    combineLatestWith(options.currentUserData$),
    map(
      ([user, userData]): FeatureToggleUser => ({
        id: Id.assert(user.id),
        email: user.email!, // SAFETY: We've already checked above
        workspaceSlugs: [...userData.workspaceSlugs],
      }),
    ),
  )

  // Identify the toggle user with the toggle client.
  const featureToggleUserIdentified$ = featureToggleUsers$.pipe(
    concatMap((user) => featureToggleClient.identify(user)),
    shareLatest(),
  )

  // Subscribe to run the pipeline
  featureToggleUserIdentified$.subscribe()

  return {
    /**
     * Emits when the feature toggle user has been identified.
     */
    featureToggleUserIdentified$,

    /**
     * Resolves when the feature toggle user has been identified.
     */
    featureToggleUserIdentified: firstValueFrom<unknown, unknown>(
      featureToggleUserIdentified$,
      {
        defaultValue: null,
      },
    ),
  }
}
